<template>
    <modal
        :show.sync="showModal"
        body-classes="p-0"
        :modal-classes="`modal-xl ${$style.modalClasses}`"
    >
        <card class="card-form mb-0">
            <template slot="header">
                <h2 class="modal-title">
                    Update Future Request #{{ delivery.id }}
                </h2>
                <h4 :class="$style.advanceNotice">
                    * Delivery requests must be placed at least 48 hours before
                    your desired delivery date.
                </h4>
            </template>
            <div :class="$style.header">
                <div :class="$style.customer">
                    <div :class="$style.label">Customer / Showroom</div>
                    <div :class="$style.title">
                        {{ delivery.customer.name }}
                    </div>
                </div>
                <div :class="$style.designer">
                    <div :class="$style.label">Designer</div>
                    <div :class="$style.title">
                        {{ designerName }}
                    </div>
                </div>
            </div>
            <el-table
                ref="table"
                :class="$style.table"
                max-height="350"
                :data="tableItems"
                @selection-change="selectedRows = $event"
            >
                <template slot="empty">
                    <el-button type="text" @click="addRequestDetailRow">
                        Add a row
                    </el-button>
                    to get started.
                </template>
                <el-table-column type="selection" />
                <el-table-column label="Manufacturer">
                    <template slot-scope="props">
                        <base-input
                            name="manufacturerName"
                            v-model="props.row.manufacturerName"
                            type="text"
                        />
                    </template>
                </el-table-column>
                <el-table-column label="MFR. Order NO.">
                    <template slot-scope="props">
                        <base-input
                            name="manufacturerOrder"
                            v-model="props.row.manufacturerOrder"
                            type="text"
                        />
                    </template>
                </el-table-column>
                <el-table-column label="P.O. No.">
                    <template slot-scope="props">
                        <base-input
                            name="purchaseOrder"
                            v-model="props.row.purchaseOrder"
                            type="text"
                        />
                    </template>
                </el-table-column>
                <el-table-column label="Job ID">
                    <template slot-scope="props">
                        <base-input
                            name="jobNumber"
                            v-model="props.row.jobNumber"
                            type="text"
                        />
                    </template>
                </el-table-column>
                <el-table-column label="QTY" width="100">
                    <template slot-scope="props">
                        <div class="form-group">
                            <masked-input
                                class="form-control"
                                name="quantity"
                                type="text"
                                v-model="props.row.quantity"
                                :mask="numberMask"
                            />
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="Materials">
                    <template slot-scope="props">
                        <div class="form-group">
                            <el-select
                                class="select-primary"
                                v-model="props.row.materials"
                                filterable
                                placeholder="Select material"
                            >
                                <el-option
                                    v-for="type in materialTypes"
                                    class="select-primary"
                                    :key="type.id"
                                    :label="type.description"
                                    :value="type.description"
                                />
                            </el-select>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="Type">
                    <template slot-scope="props">
                        <div class="form-group">
                            <el-select
                                v-model="props.row.unitType"
                                class="select-primary"
                                placeholder="Select size"
                            >
                                <el-option
                                    v-for="type in materialSizeTypes"
                                    class="select-primary"
                                    :key="type"
                                    :label="type"
                                    :value="type"
                                />
                            </el-select>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
            <div :class="$style.tableActionGroup">
                <el-button
                    type="primary"
                    icon="el-icon-plus"
                    plain
                    round
                    size="small"
                    @click="addRequestDetailRow"
                >
                    Add Row
                </el-button>
                <el-button
                    type="danger"
                    icon="el-icon-delete"
                    plain
                    round
                    size="small"
                    :disabled="selectedRows.length === 0"
                    @click="deleteSelectedRows"
                >
                    Delete
                    <span v-if="selectedRows.length">
                        ({{ selectedRows.length }})
                    </span>
                </el-button>
            </div>
            <el-divider />
            <ValidationObserver v-slot="{ invalid, validate }">
                <form
                    class="mt-3"
                    autocomplete="off"
                    @submit.prevent="validate().then(updateFutureRequest)"
                >
                    <div class="row">
                        <div class="col-3">
                            <base-input
                                name="location"
                                type="text"
                                v-model="delivery.locationName"
                                label="Location Name"
                            />
                        </div>
                        <div class="col-3">
                            <ValidationProvider
                                name="Delivery Date"
                                v-slot="{ errors }"
                                rules="required"
                            >
                                <base-input
                                    name="deliveryDate"
                                    type="date"
                                    label="Desired Delivery Date"
                                    :error="errors[0]"
                                >
                                    <el-date-picker
                                        :picker-options="{
                                            disabledDate: isDeliveryDateInvalid,
                                        }"
                                        v-model="delivery.deliveryDate"
                                        format="MM/dd/yyyy"
                                        type="date"
                                        placeholder="Select date"
                                    />
                                </base-input>
                            </ValidationProvider>
                        </div>
                        <div class="col-6">
                            <ValidationProvider
                                name="Delivery Window"
                                v-slot="{ errors }"
                                rules="required"
                            >
                                <label>Desired Delivery Window</label>
                                <div class="form-group">
                                    <el-radio-group
                                        v-model="deliveryWindow"
                                        size="medium"
                                        style="display: block"
                                    >
                                        <el-radio-button
                                            v-for="option in deliveryWindowOptions"
                                            :key="option.value"
                                            :label="option.label"
                                        />
                                    </el-radio-group>
                                    <label class="error">{{ errors[0] }}</label>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-4">
                            <ValidationProvider
                                name="Job Site Contact Name"
                                v-slot="{ errors }"
                                rules="required"
                            >
                                <base-input
                                    name="jobSiteContactName"
                                    type="text"
                                    v-model="delivery.contacts[0].name"
                                    label="Job Site Contact Name"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                        <div class="col-4">
                            <ValidationProvider
                                name="Job Site Contact Phone"
                                v-slot="{ errors }"
                                rules="required"
                            >
                                <div class="form-group has-label">
                                    <label>Job Site Contact Phone</label>
                                    <masked-input
                                        class="form-control"
                                        name="jobSiteContactPhone"
                                        autocomplete="off"
                                        type="tel"
                                        :mask="phoneMask"
                                        :guide="true"
                                        placeholder="(123) 456-7890"
                                        v-model="delivery.contacts[0].phone"
                                        :placeholder-char="'\u2000'"
                                        :error="errors[0]"
                                    />
                                </div>
                            </ValidationProvider>
                        </div>
                        <div class="col-4">
                            <ValidationProvider
                                name="Job Site Contact Email"
                                v-slot="{ errors }"
                                rules="required"
                            >
                                <base-input
                                    name="jobSiteContactEmail"
                                    type="text"
                                    v-model="delivery.contacts[0].email"
                                    label="Job Site Contact Email"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Delivery Address"
                                rules="required"
                            >
                                <base-input
                                    v-model="delivery.deliveryAddress"
                                    name="address"
                                    type="text"
                                    label="Delivery Address"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Delivery City"
                                rules="required"
                            >
                                <base-input
                                    v-model="delivery.deliveryCity"
                                    name="city"
                                    type="text"
                                    label="Delivery City"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Delivery Zip"
                                rules="required"
                            >
                                <div class="form-group has-label">
                                    <label>Delivery Zip</label>
                                    <masked-input
                                        v-model="delivery.deliveryZip"
                                        class="form-control"
                                        name="zip"
                                        autocomplete="off"
                                        type="text"
                                        :mask="zipMask"
                                        :guide="true"
                                        placeholder-char="#"
                                        :error="errors[0]"
                                    />
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Deliver Into"
                                rules="required"
                            >
                                <label>Deliver Into</label>
                                <div class="form-group">
                                    <el-radio-group
                                        v-model="delivery.deliverInto"
                                    >
                                        <el-radio-button label="House" />
                                        <el-radio-button label="Garage" />
                                        <el-radio-button label="Other" />
                                    </el-radio-group>
                                    <label class="error">{{ errors[0] }}</label>
                                </div>
                            </ValidationProvider>
                        </div>
                        <div class="col-2">
                            <label>Elevator?</label>
                            <div class="form-group">
                                <el-switch
                                    v-model="delivery.hasElevator"
                                    active-text="Yes"
                                    inactive-text="No"
                                />
                            </div>
                        </div>
                        <div class="col-3">
                            <label># of Interior Stairs</label>
                            <el-input-number
                                v-model="delivery.interiorStairsCount"
                                :min="0"
                            />
                        </div>
                        <div class="col-3">
                            <label># of Exterior Stairs</label>
                            <el-input-number
                                v-model="delivery.exteriorStairsCount"
                                :min="0"
                            />
                        </div>
                    </div>
                    <div
                        v-for="(contact, index) in additionalContacts"
                        :key="index"
                        class="row"
                    >
                        <div class="col-4">
                            <base-input
                                v-model="delivery.contacts[index + 1].name"
                                v-focus
                                focus
                                :name="`jobSiteContactName${index + 1}`"
                                type="text"
                                :label="`Job Site Contact Name ${index + 1}`"
                            />
                        </div>
                        <div class="col-4">
                            <div class="form-group has-label">
                                <label>
                                    Job Site Contact Phone
                                    {{ index + 1 }}
                                </label>
                                <masked-input
                                    v-model="delivery.contacts[index + 1].phone"
                                    class="form-control"
                                    :name="`jobSiteContactPhone${index + 1}`"
                                    autocomplete="off"
                                    type="tel"
                                    :mask="phoneMask"
                                    :guide="true"
                                    placeholder="(123) 456-7890"
                                    :placeholder-char="'\u2000'"
                                />
                            </div>
                        </div>
                        <div class="col-4">
                            <div class="form-group has-label">
                                <base-input
                                    v-model="delivery.contacts[index + 1].email"
                                    :name="`jobSiteContactEmail${index + 1}`"
                                    type="text"
                                    :label="`Job Site Contact Email ${index + 1}`"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="mt-3">
                        <base-button
                            class="pl-0"
                            link
                            type="primary"
                            @click="delivery.contacts.push({})"
                        >
                            Add Additional Contact
                        </base-button>
                    </div>
                    <div>
                        <base-input
                            v-if="showNotesTextArea"
                            name="deliveryNotes"
                            label="Delivery Notes/Special Instructions"
                        >
                            <textarea
                                v-model="delivery.deliveryNotes"
                                v-focus
                                style="max-height: unset"
                                class="form-control"
                                rows="3"
                            />
                        </base-input>
                        <base-button
                            v-if="!showNotesTextArea"
                            class="pl-0"
                            link
                            type="primary"
                            @click="showNotesTextArea = true"
                        >
                            Add Delivery Notes or Special Instructions
                        </base-button>
                    </div>
                    <div>
                        <p class="small bold">
                            * It is your responsibility as the client to make
                            sure you assign the items in this Future Delivery
                            Request to the matching Receipt Log. If you do not
                            assign this Future Delivery Request to a matching
                            Receipt Log, your items will NOT be delivered when
                            you expect.
                        </p>
                        <p class="small">
                            * Please be sure to include in the Special
                            Instructions if your delivery requires a special
                            sized truck; if the site is difficult to access, or
                            has steep or narrow roads and/or driveways; if there
                            is a long carry; if we need a gate or door code,
                            etc. Please note that these conditions may cause
                            delays in the delivery and may increase the time we
                            spend on site which could result in additional
                            charges.
                        </p>
                        <p class="small">
                            * We no longer do 3rd party billing. We will bill
                            you for the delivery -- if you would like your
                            customer to pay for the delivery, we are happy to
                            accept a check from your customer or you may call us
                            with their credit card information. All new accounts
                            will be required to have a credit card on file for a
                            minimum of 5 deliveries at which time we will
                            evaluate the possibility of an open account.
                        </p>
                    </div>
                    <el-divider />
                    <div class="d-flex">
                        <el-checkbox
                            disabled
                            :class="$style.checkbox"
                            :value="true"
                        />
                        <span class="ml-2 d-flex">
                            I have read and agree to the above and to the
                            <base-button
                                v-if="materialType === 'Cabinets'"
                                link
                                :class="$style.link"
                                type="primary"
                                @click="showCabinetTermsAndConditions = true"
                            >
                                terms and conditions.
                            </base-button>
                            <base-button
                                v-else-if="materialType === 'Furniture'"
                                link
                                :class="$style.link"
                                type="primary"
                                @click="showFurnitureTermsAndConditions = true"
                            >
                                terms and conditions.
                            </base-button>
                            <base-button
                                v-else
                                link
                                :class="$style.link"
                                @click="
                                    showCabinetTermsAndConditions = true;
                                    showFurnitureTermsAndConditions = true;
                                "
                                type="primary"
                            >
                                terms and conditions.
                            </base-button>
                        </span>
                        <label class="error">{{ errors[0] }}</label>
                    </div>
                    <div class="d-flex mt-2">
                        <a href="/docs/baserates.pdf">
                            Download & view current city/zip code rates.
                        </a>
                    </div>
                    <div :class="$style.actions">
                        <base-button
                            type="default"
                            class="btn-fill"
                            @click="reset()"
                        >
                            Cancel
                        </base-button>
                        <div :class="$style.right">
                            <base-button
                                id="update"
                                :disabled="invalid || isUnModified"
                                native-type="submit"
                                type="primary"
                                class="btn-fill"
                            >
                                Update
                            </base-button>
                            <base-button
                                v-if="showDeleteButton"
                                type="danger"
                                class="btn-fill"
                                @click="confirmDeletion"
                            >
                                Delete
                            </base-button>
                            <base-button
                                v-if="showConfirmButton"
                                type="info"
                                class="btn-fill"
                                @click="confirmMarkConfirmed"
                            >
                                Mark Confirmed
                            </base-button>
                            <base-button
                                id="assign"
                                :disabled="!isUnModified"
                                type="info"
                                class="btn-fill"
                                @click="showReceiptLogModal = true"
                            >
                                Assign to Receipt Log(s)
                            </base-button>
                        </div>
                    </div>
                </form>
            </ValidationObserver>
        </card>
        <cabinet-terms-and-conditions-modal
            :show.sync="showCabinetTermsAndConditions"
        />
        <furniture-terms-and-conditions-modal
            :show.sync="showFurnitureTermsAndConditions"
        />
        <receipt-log-select-modal
            v-if="showReceiptLogModal"
            @close="showReceiptLogModal = false"
            @receipt-logs-selected="$emit('assign-to-receipt-logs', $event)"
        />
    </modal>
</template>

<script>
import swal from "sweetalert2";
import moment from "moment-timezone";
import _ from "lodash";
import MaskedInput from "vue-text-mask";
import { phoneMask, zipMask, numberMask } from "src/helpers/inputMasks";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Modal } from "src/components/UI";
import {
    Button,
    DatePicker,
    Switch,
    InputNumber,
    RadioGroup,
    RadioButton,
    Checkbox,
    Divider,
} from "element-ui";
import CabinetTermsAndConditionsModal from "src/modules/DeliveryRequest/NewDeliveryRequest/_components/CabinetTermsAndConditionsModal";
import FurnitureTermsAndConditionsModal from "src/modules/DeliveryRequest/NewDeliveryRequest/_components/FurnitureTermsAndConditionsModal";
import ReceiptLogSelectModal from "./ReceiptLogSelectModal";

export default {
    name: "FutureDeliveryRequestDetail",
    components: {
        Modal,
        MaskedInput,
        [Button.name]: Button,
        [DatePicker.name]: DatePicker,
        [Switch.name]: Switch,
        [InputNumber.name]: InputNumber,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Checkbox.name]: Checkbox,
        [Divider.name]: Divider,
        ValidationObserver,
        ValidationProvider,
        CabinetTermsAndConditionsModal,
        FurnitureTermsAndConditionsModal,
        ReceiptLogSelectModal,
    },
    props: {
        futureRequest: {
            type: Object,
            required: true,
        },
        materialTypes: {
            type: Array,
            required: true,
        },
        materialSizeTypes: {
            type: Array,
            required: true,
        },
        show: {
            type: Boolean,
            required: true,
        },
        showDeleteButton: {
            type: Boolean,
            required: true,
        },
        showConfirmButton: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            delivery: {},
            selectedRows: [],
            showCabinetTermsAndConditions: false,
            showFurnitureTermsAndConditions: false,
            phoneMask,
            zipMask,
            numberMask,
            showReceiptLogModal: false,
            selectedDeliveryWindow: null,
            selectedDeliverInto: null,
            selectedElevator: null,
            deliveryWindowOptions: [
                { value: "Morning", label: "9AM - 11AM" },
                { value: "Midday", label: "11AM - 1PM" },
                { value: "Afternoon", label: "1PM - 3PM" },
                { value: "Any", label: "Any" },
            ],
            showNotesTextArea: false,
            showModal: false,
        };
    },
    computed: {
        isUnModified() {
            return _.isEqual(this.futureRequest, this.delivery);
        },
        designerName() {
            return this.delivery.designer?.fullName;
        },
        additionalContacts() {
            // exclude first result
            return this.delivery.contacts.slice(1);
        },
        deliveryWindow: {
            get() {
                return this.selectedDeliveryWindow;
            },
            set(window) {
                this.selectedDeliveryWindow = window;
                // get the value from the given label
                const { value } = this.deliveryWindowOptions.find(
                    (option) => option.label === window,
                );
                this.delivery.deliveryWindow = value;
            },
        },
        tableItems() {
            return this.delivery.requestDetails;
        },
        materialType() {
            const allTypes = this.delivery.requestDetails.map(
                (detail) => detail.materials,
            );
            const types = [...new Set(allTypes)];
            if (types.includes("Cabinets") && types.includes("Furniture")) {
                return "Both";
            }
            if (types.includes("Cabinets")) return "Cabinets";
            if (types.includes("Furniture")) return "Furniture";

            return "Unknown";
        },
    },
    watch: {
        show(val) {
            this.showModal = val;
        },
        showModal(val) {
            this.$emit("update:show", val);
        },
        futureRequest: {
            immediate: true,
            handler(request) {
                this.delivery = _.cloneDeep(request);
            },
        },
    },
    created() {
        this.showModal = this.show;
        this.selectedDeliveryWindow = this.deliveryWindowOptions.find(
            (option) => option.value === this.delivery.deliveryWindow,
        ).label;
    },
    methods: {
        async confirmMarkConfirmed() {
            const { value } = await swal({
                title: "Confirm",
                text: "Are you sure you want to mark this future request as confirmed?",
                type: "question",
                reverseButtons: true,
                showConfirmButton: true,
                confirmButtonText: "Confirm",
                showCancelButton: true,
            });
            if (value) {
                this.$emit("confirmRequest");
            }
        },
        async confirmDeletion() {
            const { value } = await swal({
                title: "Confirm Deletion",
                text: "Are you sure you want to delete this future request?",
                type: "warning",
                reverseButtons: true,
                showConfirmButton: true,
                confirmButtonText: "Delete",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
            });
            if (value) {
                this.$emit("deleteRequest");
            }
        },
        deleteSelectedRows() {
            const ids = this.selectedRows.map((row) => row.detailId);
            ids.forEach((id) => {
                const index = this.delivery.requestDetails.findIndex(
                    (d) => d.detailId === id,
                );
                if (index !== -1) {
                    this.delivery.requestDetails.splice(index, 1);
                }
            });
            this.selectedRows = [];
        },
        addRequestDetailRow() {
            this.delivery.requestDetails.push({
                detailId: this.generateRandomID(),
            });
        },
        isDeliveryDateInvalid(date) {
            const fortyEightHoursFromNow = moment().add(48, "hours");
            const threePMHour = 15;
            const compareDate = moment(date).hour(threePMHour);
            return fortyEightHoursFromNow > compareDate;
        },
        updateFutureRequest() {
            this.$emit("updateFutureDeliveryRequest", {
                ...this.delivery,
                requestDetails: this.delivery.requestDetails
                    // remove blanks
                    .filter((detail) => !_.isEmpty(detail))
                    .map((d, index) => ({
                        ...d,
                        detailId: index + 1,
                        quantity: Number(d.quantity),
                    })),
                exteriorStairsCount:
                    parseInt(this.delivery.exteriorStairsCount, 10) || 0,
                interiorStairsCount:
                    parseInt(this.delivery.interiorStairsCount, 10) || 0,
            });
        },
        reset() {
            this.selectedDeliveryWindow = null;
            this.showModal = false;
            this.$emit("cancel");
        },
        generateRandomID() {
            return Math.random().toString(36).substring(2, 15);
        },
    },
};
</script>

<style module lang="scss">
@import "assets/sass/dashboard/custom/_variables.scss";

.modalClasses {
    min-width: 750px;
}

.header {
    display: flex;
    padding-left: 10px;
    padding-right: 30px;

    .title {
        font-size: 18px;
        color: $black;
        font-weight: 600;
    }

    .customer {
        flex-basis: 70%;
    }
}

.actions {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
}

.actions .right > * {
    margin-right: 10px;
    margin-left: 10px;
}

.checkbox {
    align-self: center;
    margin: 0 !important;
}

.link {
    font-weight: 400;
    padding: 0px;
    margin: 0px;
    margin-left: 3px;
    line-height: 1.5;
}

.tableActionGroup {
    display: flex;
}

.table tr:hover td {
    background: transparent !important;
}

.table table {
    margin-bottom: 0;
}

.advanceNotice {
    margin-top: 20px;
    font-weight: bold;
    color: hsla(349, 72%, 48%, 1) !important;
}
</style>
